// Custom hook for Signin page logic
import { Form } from 'antd';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { signinSchema } from '@validators/outside.schema';
import { useLoginMutation } from './useAuthMutations';
import { getDashboardRoute } from '@utils/routing';
import { useAuthStore } from '@stores/auth.store';

/**
 * @param {'owner'|'tenant'} portal
 */
export const useSignin = (portal) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const loginMutation = useLoginMutation();

    const onFinish = async (values) => {
        try {
            const parsedValues = signinSchema.parse(values);

            const res = await loginMutation.mutateAsync({
                email: parsedValues.email,
                password: parsedValues.password,
                portal,
            });

            if (parsedValues.remember) {
                localStorage.setItem('remember_me', 'true');
            }

            const userRole = res?.user?.role || useAuthStore.getState().role || useAuthStore.getState().user?.role;
            const dashboardRoute = getDashboardRoute(userRole);
            navigate(dashboardRoute, { replace: true });
        } catch (error) {
            if (error instanceof z.ZodError) {
                const fieldErrors = {};
                error.errors.forEach((err) => {
                    fieldErrors[err.path[0]] = err.message;
                });
                form.setFields(
                    Object.keys(fieldErrors).map((field) => ({
                        name: field,
                        errors: [fieldErrors[field]],
                    }))
                );
                showMessage('error', fieldErrors[Object.keys(fieldErrors)[0]]);
            }
        }
    };

    return {
        form,
        loading: loginMutation.isPending,
        onFinish,
    };
};
